const ACCORDION_LABELS = new Map([
  ['details', 'Details'],
  ['product details', 'Details'],
  ['fit', 'Fit'],
  ['fit & sizing', 'Fit'],
  ['fit and sizing', 'Fit'],
]);

/**
 * Turns the Details and Fit headings inside a product description into
 * collapsible rows without requiring product data to be migrated.
 */
class PdpDescriptionAccordions extends HTMLElement {
  connectedCallback() {
    this.enhance();
  }

  /**
   * Enhance the current product description. This is intentionally safe to
   * call more than once: linked-product swaps morph new description markup
   * into this existing custom element without reconnecting it.
   */
  enhance() {
    const source = this.querySelector('[data-pdp-description-source] rte-formatter');
    if (!(source instanceof HTMLElement)) return;

    const sourceNodes = Array.from(source.childNodes);
    const markers = sourceNodes.flatMap((node, index) => {
      if (!(node instanceof HTMLElement)) return [];

      const key = this.#getSectionKey(node);
      return key ? [{ key, node, index }] : [];
    });

    if (markers.length === 0) return;

    // A linked-product morph can leave the custom-element instance connected
    // while replacing its source content. Remove any generated rows from the
    // prior listing before rebuilding from the newly received description.
    this.querySelectorAll(':scope > .pdp-description-accordions').forEach((accordions) => {
      accordions.remove();
    });
    this.dataset.enhanced = 'false';
    source.hidden = false;

    const sections = markers.flatMap((marker, markerIndex) => {
      const nextMarker = markers[markerIndex + 1];
      const nodes = sourceNodes.slice(marker.index + 1, nextMarker?.index ?? sourceNodes.length);
      const hasContent = nodes.some((node) => {
        if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent?.trim());
        if (!(node instanceof HTMLElement)) return false;
        return Boolean(node.textContent?.trim() || node.querySelector('img, video, iframe, table'));
      });

      return hasContent ? [{ ...marker, nodes }] : [];
    });

    if (sections.length === 0) return;

    const accordions = document.createElement('div');
    accordions.className = 'pdp-description-accordions';

    for (const section of sections) {
      const accordion = document.createElement('accordion-custom');
      accordion.className = 'pdp-description-accordion';

      const details = document.createElement('details');
      details.className = 'details pdp-description-accordion__details';

      const summary = document.createElement('summary');
      summary.className = 'details__header pdp-description-accordion__summary';

      const label = document.createElement('span');
      label.textContent = ACCORDION_LABELS.get(section.key) ?? section.node.textContent?.trim() ?? '';

      const icon = document.createElement('span');
      icon.className = 'pdp-description-accordion__icon';
      icon.setAttribute('aria-hidden', 'true');

      const content = document.createElement('div');
      content.className = 'details-content pdp-description-accordion__content';
      content.append(...section.nodes);

      summary.append(label, icon);
      details.append(summary, content);
      accordion.append(details);
      accordions.append(accordion);
      section.node.remove();
    }

    source.after(accordions);
    this.#placeProductExtras(accordions);
    this.dataset.enhanced = 'true';

    const hasVisibleIntro = Array.from(source.childNodes).some((node) => {
      if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent?.trim());
      if (!(node instanceof HTMLElement)) return false;
      return Boolean(node.textContent?.trim() || node.querySelector('img, video, iframe, table'));
    });

    source.hidden = !hasVisibleIntro;
  }

  /**
   * Product features are a separate Shopify block. Place the minimalist
   * feature list after the visible description, then append its standalone
   * accordions after Details/Fit so every accordion uses the same treatment.
   * A short-lived observer covers Theme Editor section re-renders where the
   * extras block is inserted just after this custom element connects.
   *
   * @param {HTMLElement} accordions
   */
  #placeProductExtras(accordions) {
    const productDetails = this.closest('.product-details');
    if (!(productDetails instanceof HTMLElement)) return;

    const placeExtras = () => {
      const extras = productDetails.querySelector('.sunice-pdp-extras');
      if (!(extras instanceof HTMLElement)) return false;

      const features = extras.querySelector('.sunice-pdp-features');
      accordions.before(extras);

      extras.querySelectorAll('[data-pdp-extra-accordion]').forEach((accordion) => {
        accordions.append(accordion);
      });

      extras.hidden = !(features instanceof HTMLElement);
      if (features instanceof HTMLElement) {
        extras.classList.add('sunice-pdp-extras--positioned');
        accordions.classList.add('pdp-description-accordions--after-extras');
      }

      return true;
    };

    if (placeExtras()) return;

    const observer = new MutationObserver(() => {
      if (!placeExtras()) return;
      observer.disconnect();
      window.clearTimeout(observerTimeout);
    });

    observer.observe(productDetails, { childList: true, subtree: true });
    const observerTimeout = window.setTimeout(() => observer.disconnect(), 2000);
  }

  /**
   * @param {HTMLElement} element
   * @returns {string | undefined}
   */
  #getSectionKey(element) {
    const normalized = element.textContent
      ?.replace(/\u00a0/g, ' ')
      .trim()
      .replace(/:$/, '')
      .toLowerCase();

    return normalized && ACCORDION_LABELS.has(normalized) ? normalized : undefined;
  }
}

if (!customElements.get('pdp-description-accordions')) {
  customElements.define('pdp-description-accordions', PdpDescriptionAccordions);

  // Cross-listing colour changes update <main> in place, so connectedCallback
  // is not guaranteed to run again. Refresh once the new listing is installed.
  document.addEventListener('variant:update', () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('pdp-description-accordions').forEach((element) => {
        if (element instanceof PdpDescriptionAccordions) element.enhance();
      });
    });
  });
}
